import React, { useContext, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import ConfirmationDialog from 'live/components/actions/ui/confirmation-dialog'
import { editCountryTreeElement, postAsset } from '../common/apis'
import { handleResponse, updateAssetTreeState } from '../common/utils'
import { configsContext } from './AssetConfigs'
import { DialogContent } from './dialogContent'
import { capitalize, cloneDeep } from 'lodash'
import { getFaildResponse, getLinesPostJson, getNamesFromTableArray } from './utils'

interface IProps {
    showModal: boolean
    setShowModal: (show: boolean) => void
    currentName?: string
    currentCountr?: string
    isEdit: boolean
}

const defaultData = [[''], [''], ['']]

export function FactoryDialog({ showModal, setShowModal, currentCountr, currentName, isEdit }: IProps): JSX.Element {
    const [tableDataArray, setTableDataArray] = useState<string[][]>(cloneDeep(defaultData))
    const [factoryName, setFactoryName] = useState(isEdit ? currentName : '')
    const [countryName, setCountryName] = useState(isEdit ? currentCountr : '')
    const [showErrors, setShowErrors] = useState(false)
    const [errorInInputs, setErrorInInputs] = useState(true)

    const { setAssetTree } = useContext(configsContext)

    function handleHide(): void {
        setShowModal(false)
        setTableDataArray(cloneDeep(defaultData))
        setFactoryName('')
        setCountryName('')
    }

    useEffect(() => {
        const isError = checkIfInputsHaveErrors()

        setErrorInInputs(isError)
        if (!isError && showErrors) setShowErrors(false)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryName, factoryName, currentCountr, currentName])

    function checkIfInputsHaveErrors(): boolean {
        const factoryDoesntHaveName = !factoryName
        const factoryDoesntHaveCountry = !countryName
        const nameDidntChange = factoryName === currentName
        const countryDidntChange = countryName === currentCountr

        if (factoryDoesntHaveName || factoryDoesntHaveCountry) return true

        return isEdit && nameDidntChange && countryDidntChange
    }

    function editCountryOrFactoryNames(): void {
        let finalResponse: Response

        editCountryTreeElement(currentName, factoryName, 'factory', currentCountr).then(factoryPutResponse => {
            if (currentName !== factoryName && currentCountr === countryName) {
                finalResponse = factoryPutResponse
            }
            editCountryTreeElement(currentCountr, countryName, 'country', factoryName)
                .then(countryPutRestponse => {
                    if (currentCountr !== countryName && currentName === factoryName) {
                        finalResponse = countryPutRestponse
                    } else if (currentCountr !== countryName && currentName !== factoryName) {
                        finalResponse = getFaildResponse(factoryPutResponse, countryPutRestponse)
                    }
                })
                .finally(() => {
                    handleResponse(finalResponse, 'factory')
                    updateAssetTreeState(finalResponse?.status, 'factory', setAssetTree)
                })
        })
    }

    function postFactory(): void {
        const params = { country: countryName?.trim(), factory: factoryName?.trim() }

        postAsset('factory', params).then(postResponse => {
            handleResponse(postResponse, 'factory')
            updateAssetTreeState(postResponse.status, 'factory', setAssetTree)
        })
    }

    function postLines(): void {
        const params = getLinesPostJson(tableDataArray, countryName?.trim(), factoryName?.trim())
        postAsset('lines', params).then(postResponse => {
            handleResponse(postResponse, 'factory')
            updateAssetTreeState(postResponse.status, 'factory', setAssetTree)
        })
    }

    function handleApply(): void {
        if (errorInInputs) {
            setShowErrors(true)
        } else if (isEdit) {
            editCountryOrFactoryNames()
        } else if (getNamesFromTableArray(tableDataArray).length) {
            postLines()
        } else {
            postFactory()
        }
    }

    return (
        <ConfirmationDialog
            show={showModal}
            onHide={handleHide}
            title={` ${capitalize(i18n(isEdit ? 'edit' : 'add new'))} ${capitalize(i18n('factory'))}`}
            btnStyle={errorInInputs ? 'notAllowed' : 'primary'}
            cancelText={i18n('cancel')}
            className={'addNewFactory-confirmation-modal modal-sm'}
            content={
                <DialogContent
                    contentType={'factory'}
                    assetName={factoryName}
                    setAssetName={setFactoryName}
                    secondaryValue={countryName}
                    setSecondaryValue={setCountryName}
                    showErrors={showErrors}
                    tableDataArray={tableDataArray}
                    setTableDataArray={setTableDataArray}
                    isEdit={isEdit}
                />
            }
            btnText={i18n(isEdit ? 'Update' : 'Save')}
            apply={handleApply}
        />
    )
}
